import { findRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { issuedLabelsOf, recommendRackBin } from './inboundRmOperator.handler'
import { userName } from './notification.shared'

/**
 * PRC-05 v7.50 — tahap batch kedatangan & kartu Hasil QC (laporan 23 R-10, U-01, U-02). Semuanya DERIVE: tidak ada
 * kolom tahap di ERD; tahap dibaca dari `rm_arrival_line.line_status`, `rm_quarantine_entry`, status WF-QC-01
 * `rm_qc_assignment`, `rm_unit.unit_status`, dan log `wxl_submission_log` (pengembali / penyetuju, pola D10).
 *
 * Tahap (prototype `C.TAHAP_RM` tanpa "Disetujui" — keputusan user Q-07, dan tanpa "Partial Palletized" — Q-09):
 * Menunggu Palletizing → Palletized → Menunggu QC → QC Berjalan → Menunggu Persetujuan → Label Tercetak → Putaway → Selesai.
 */

export const ARRIVAL_STAGES = [
  'Menunggu Palletizing',
  'Palletized',
  'Menunggu QC',
  'QC Berjalan',
  'Menunggu Persetujuan',
  'Label Tercetak',
  'Putaway',
  'Selesai'
] as const

export type ArrivalStage = (typeof ARRIVAL_STAGES)[number]

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const statusCodeOf = (row: LocalRow | undefined) => String(findRow('wml_status', idOf(row?.status_id))?.code ?? '')

const STAGE_INDEX = Object.fromEntries(ARRIVAL_STAGES.map((stage, index) => [stage, index])) as Record<
  ArrivalStage,
  number
>

/* ------------------------------------------------------------ log */

const logsOf = (assignment: LocalRow, actionName: string) => {
  const action = readTable('wml_action').find(row => row.name === actionName)
  return readTable('wxl_submission_log')
    .filter(log => idOf(log.submission_id) === idOf(assignment.submission_id) && idOf(log.action_id) === action?.id)
    .sort((a, b) => String(a.date_created).localeCompare(String(b.date_created)))
}

const lastLog = (assignment: LocalRow, actionName: string) => {
  const logs = logsOf(assignment, actionName)
  const log = logs[logs.length - 1]
  return log ? { by: idOf(log.user_created), by_name: userName(log.user_created), at: String(log.date_created) } : null
}

/** Assignment `QC_IN_PROGRESS` yang pernah dikembalikan admin = sedang dikembalikan (START hanya dari ASSIGNED). */
export function returnInfoOf(assignment: LocalRow) {
  if (statusCodeOf(assignment) !== 'QC_IN_PROGRESS') return null
  const logs = logsOf(assignment, 'ACT_QC_RETURN')
  const log = logs[logs.length - 1]
  if (!log) return null
  return {
    by: idOf(log.user_created),
    by_name: userName(log.user_created),
    at: String(log.date_created),
    reason: String(log.reason ?? '')
  }
}

/* ---------------------------------------------------------- tahap */

const entriesOfLine = (lineId: string) =>
  readTable('rm_quarantine_entry').filter(entry => idOf(entry.arrival_line_id) === lineId)

const assignmentsOfEntries = (entryIds: string[]) =>
  readTable('rm_qc_assignment').filter(row => entryIds.includes(idOf(row.quarantine_entry_id)))

const unitStoredStatus = ['IN_STOCK', 'NG_IN_QUARANTINE']

function assignmentStage(assignment: LocalRow): number {
  switch (statusCodeOf(assignment)) {
    case 'QC_ASSIGNED':
      return STAGE_INDEX['Menunggu QC']
    case 'QC_IN_PROGRESS':
      return STAGE_INDEX['QC Berjalan']
    case 'QC_PENDING_APPROVAL':
      return STAGE_INDEX['Menunggu Persetujuan']
    case 'QC_IN_STORAGE':
      return STAGE_INDEX.Selesai
    case 'QC_DONE': {
      const units = [assignment.unit_id, assignment.ng_unit_id]
        .map(id => findRow('rm_unit', idOf(id)))
        .filter(Boolean) as LocalRow[]
      if (units.length && units.every(unit => unitStoredStatus.includes(String(unit.unit_status))))
        return STAGE_INDEX.Selesai
      const started = units.some(unit =>
        readTable('rm_putaway_task_item').some(item => idOf(item.unit_id) === unit.id && Number(item.qty_placed) > 0)
      )
      return started ? STAGE_INDEX.Putaway : STAGE_INDEX['Label Tercetak']
    }
    default:
      return STAGE_INDEX['Menunggu QC']
  }
}

/** Tahap satu batch (baris kedatangan) — batch yang baru sebagian dipalletkan tetap "Menunggu Palletizing". */
export function lineStage(line: LocalRow) {
  const entries = entriesOfLine(line.id)
  const pallets = [
    ...new Set(
      [
        ...readTable('rm_pallet_content')
          .filter(content => idOf(content.arrival_line_id) === line.id)
          .map(content => idOf(content.pallet_id)),
        ...entries.map(entry => idOf(entry.pallet_id))
      ].filter(Boolean)
    )
  ]
    .map(id => String(findRow('pallet', id)?.code ?? ''))
    .filter(Boolean)
    .sort()
  const assignments = assignmentsOfEntries(entries.map(entry => entry.id))
  const returned = assignments.map(returnInfoOf).find(Boolean) ?? null
  let index: number
  if (line.line_status !== 'PALLETIZED') index = STAGE_INDEX['Menunggu Palletizing']
  else if (!entries.length) index = STAGE_INDEX.Palletized
  else if (!assignments.length) index = STAGE_INDEX['Menunggu QC']
  else {
    const unscheduled = entries.some(
      entry =>
        assignments
          .filter(row => idOf(row.quarantine_entry_id) === entry.id)
          .reduce((sum, row) => sum + Number(row.qty ?? 0), 0) < Number(entry.qty ?? 0)
    )
    index = Math.min(...assignments.map(assignmentStage), ...(unscheduled ? [STAGE_INDEX['Menunggu QC']] : []))
  }
  return { stage: ARRIVAL_STAGES[index], index, pallets, returned }
}

/** Satu chip per kedatangan: tahap paling awal + `n/total` bila batch lain sudah lebih maju (prototype `batchStatusChip`). */
export function arrivalStageSummary(arrivalId: string) {
  const lines = readTable('rm_arrival_line').filter(line => idOf(line.arrival_id) === arrivalId)
  if (!lines.length) return null
  const stages = lines.map(lineStage)
  const min = Math.min(...stages.map(stage => stage.index))
  const count = stages.filter(stage => stage.index === min).length
  return { stage: ARRIVAL_STAGES[min], index: min, count, total: lines.length }
}

/* ----------------------------------------------------- kartu Hasil QC */

const SHOWN_STATUSES = ['QC_PENDING_APPROVAL', 'QC_DONE', 'QC_IN_STORAGE']

type PackageState = 'PENDING' | 'RETURNED' | 'ISSUED' | 'STORED'

const lineOf = (assignment: LocalRow) => {
  const entry = findRow('rm_quarantine_entry', idOf(assignment.quarantine_entry_id))
  return { entry, line: findRow('rm_arrival_line', idOf(entry?.arrival_line_id)) }
}

function packageRow(assignment: LocalRow) {
  const { entry, line } = lineOf(assignment)
  const material = findRow('mt_material', idOf(line?.material_id))
  const unit = findRow('mt_unit', idOf(line?.unit_id))
  const arrival = findRow('rm_arrival', idOf(line?.arrival_id))
  const reasons = (Array.isArray(assignment.ng_reason_ids) ? assignment.ng_reason_ids : [])
    .map(id => String(findRow('mt_reject_type', id)?.name ?? ''))
    .filter(Boolean)
  return {
    assignment_id: assignment.id,
    assignment_no: assignment.assignment_no,
    status: statusCodeOf(assignment),
    quarantine_no: entry?.quarantine_no ?? null,
    arrival_no: arrival?.arrival_no ?? null,
    material: material ? { id: material.id, code: material.code, name: material.name } : null,
    batch_no: line?.batch_no ?? null,
    unit: unit?.name ?? '',
    qty: Number(assignment.qty ?? 0),
    good: Number(assignment.qty_good ?? 0),
    ng: Number(assignment.qty_ng ?? 0),
    ng_reason: assignment.ng_reason ?? (reasons.length ? reasons.join(', ') : null),
    good_unit_code: assignment.good_unit_code ?? null,
    ng_unit_code: assignment.ng_unit_code ?? null
  }
}

/**
 * Kartu Hasil QC untuk satu kedatangan: satu kartu = satu pallet yang dinilai satu operator (paket persetujuan).
 * Kartu memuat SELURUH isi paket — persetujuan berlaku untuk seluruh pallet walau sebagian batch-nya berasal dari
 * kedatangan lain (kolom `arrival_no` menandainya).
 */
export function qcPackagesOfArrival(arrivalId: string) {
  const lineIds = new Set(
    readTable('rm_arrival_line')
      .filter(line => idOf(line.arrival_id) === arrivalId)
      .map(line => line.id)
  )
  const entryIds = readTable('rm_quarantine_entry')
    .filter(entry => lineIds.has(idOf(entry.arrival_line_id)))
    .map(entry => entry.id)
  const touched = assignmentsOfEntries(entryIds).filter(
    row => SHOWN_STATUSES.includes(statusCodeOf(row)) || returnInfoOf(row)
  )
  const keys = new Map<string, { palletId: string | null; entryId: string; operatorId: string }>()
  touched.forEach(row => {
    const entry = findRow('rm_quarantine_entry', idOf(row.quarantine_entry_id))
    const palletId = idOf(entry?.pallet_id) || null
    const key = `${palletId ?? `QRT:${entry?.id}`}|${idOf(row.operator_id)}`
    if (!keys.has(key)) keys.set(key, { palletId, entryId: String(entry?.id), operatorId: idOf(row.operator_id) })
  })
  return [...keys.entries()].map(([key, { palletId, entryId, operatorId }]) => {
    const members = readTable('rm_qc_assignment')
      .filter(row => {
        const entry = findRow('rm_quarantine_entry', idOf(row.quarantine_entry_id))
        const samePallet = palletId ? idOf(entry?.pallet_id) === palletId : entry?.id === entryId
        return samePallet && idOf(row.operator_id) === operatorId
      })
      .filter(row => SHOWN_STATUSES.includes(statusCodeOf(row)) || returnInfoOf(row))
      .sort((a, b) => String(a.assignment_no).localeCompare(String(b.assignment_no)))
    const pending = members.filter(row => statusCodeOf(row) === 'QC_PENDING_APPROVAL')
    const returned = members.map(returnInfoOf).find(Boolean) ?? null
    const issued = members.filter(row => ['QC_DONE', 'QC_IN_STORAGE'].includes(statusCodeOf(row)))
    const state: PackageState = pending.length
      ? 'PENDING'
      : returned
        ? 'RETURNED'
        : issued.length && issued.every(row => statusCodeOf(row) === 'QC_IN_STORAGE')
          ? 'STORED'
          : 'ISSUED'
    const rows = members.map(packageRow)
    const saved = pending.length ? lastLog(pending[0], 'ACT_QC_RECORD') : null
    const approved =
      issued
        .map(row => lastLog(row, 'ACT_QC_APPROVE_PRINT'))
        .filter(Boolean)
        .sort((a, b) => String(b?.at).localeCompare(String(a?.at)))[0] ?? null
    const pallet = palletId ? findRow('pallet', palletId) : undefined
    /* Pratinjau stiker sebelum terbit memakai kode yang dipesan (R-09) — sama persis dengan kode yang akan terbit. */
    const preview = pending.flatMap(row => {
      const info = packageRow(row)
      const material = info.material
      const reco = info.good > 0 && material ? recommendRackBin(String(material.id)) : null
      return [
        ...(info.good > 0
          ? [
              {
                unit_qr: String(info.good_unit_code ?? ''),
                flag: 'REGULAR' as const,
                qty: info.good,
                unit: info.unit,
                material_code: String(material?.code ?? ''),
                material_name: String(material?.name ?? ''),
                batch_no: String(info.batch_no ?? ''),
                recommended: (reco?.code as string | undefined) ?? null,
                ticket_no: null
              }
            ]
          : []),
        ...(info.ng > 0
          ? [
              {
                unit_qr: String(info.ng_unit_code ?? ''),
                flag: 'NG' as const,
                qty: info.ng,
                unit: info.unit,
                material_code: String(material?.code ?? ''),
                material_name: String(material?.name ?? ''),
                batch_no: String(info.batch_no ?? ''),
                recommended: 'QN-NG',
                ticket_no: null
              }
            ]
          : [])
      ]
    })
    const task = issued.length
      ? readTable('rm_putaway_task_item')
          .filter(item => issued.some(row => idOf(row.unit_id) === idOf(item.unit_id)))
          .map(item => findRow('rm_putaway_task', idOf(item.task_id)))
          .find(Boolean)
      : undefined
    return {
      key,
      pallet: pallet ? { id: pallet.id, code: pallet.code } : null,
      operator: { id: operatorId, name: userName(operatorId) },
      state,
      rows,
      totals: {
        qty: rows.reduce((sum, row) => sum + row.qty, 0),
        good: rows.reduce((sum, row) => sum + row.good, 0),
        ng: rows.reduce((sum, row) => sum + row.ng, 0)
      },
      saved,
      approved,
      returned,
      putaway_task_no: task?.task_no ?? null,
      preview_labels: preview,
      issued_labels: issuedLabelsOf(issued)
    }
  })
}

/** `GET /api/v1/wms/inbound-receiving/arrival-detail?arrival_no=` — halaman Detail Kedatangan Material (U-01). */
export function arrivalDetail(arrivalNo: string) {
  const arrival = readTable('rm_arrival').find(row => row.arrival_no === arrivalNo)
  if (!arrival) return null
  const supplier = findRow('mt_companies', idOf(arrival.supplier_id))
  /* Urutan baris = urutan dicatat di Surat Jalan (prototype `kdBatchesFor`). */
  const lines = readTable('rm_arrival_line').filter(line => idOf(line.arrival_id) === arrival.id)
  const batches = lines.map(line => {
    const material = findRow('mt_material', idOf(line.material_id))
    const unit = findRow('mt_unit', idOf(line.unit_id))
    const placed = readTable('rm_pallet_content')
      .filter(content => idOf(content.arrival_line_id) === line.id)
      .reduce((sum, content) => sum + Number(content.qty ?? 0), 0)
    return {
      id: line.id,
      material: material ? { id: material.id, code: material.code, name: material.name } : null,
      batch_no: line.batch_no,
      po_no: line.po_no,
      qty: Number(line.qty ?? 0),
      unit: unit?.name ?? '',
      line_status: line.line_status,
      remaining: line.line_status === 'PALLETIZED' ? 0 : Math.max(0, Number(line.qty ?? 0) - placed),
      ...lineStage(line)
    }
  })
  const packages = qcPackagesOfArrival(arrival.id)
  const approved =
    packages
      .map(item => item.approved)
      .filter(Boolean)
      .sort((a, b) => String(b?.at).localeCompare(String(a?.at)))[0] ?? null
  return {
    arrival: {
      id: arrival.id,
      arrival_no: arrival.arrival_no,
      sj_no: arrival.sj_no,
      supplier: supplier ? { id: supplier.id, name: supplier.name } : null,
      received_at: arrival.received_at ?? arrival.date_created,
      recorded_by: userName(arrival.user_created)
    },
    stage: arrivalStageSummary(arrival.id),
    batches,
    pallets: [...new Set(batches.flatMap(batch => batch.pallets))].sort(),
    unpalletized: batches.filter(batch => batch.line_status !== 'PALLETIZED').length,
    approved,
    packages
  }
}

/** `GET /api/v1/wms/inbound-receiving/qc-package/pending` — pengingat Admin di daftar (paket menunggu persetujuan). */
export function pendingQcPackages() {
  const pending = readTable('rm_qc_assignment').filter(row => statusCodeOf(row) === 'QC_PENDING_APPROVAL')
  const keys = new Set<string>()
  const arrivals = new Set<string>()
  pending.forEach(row => {
    const { entry, line } = lineOf(row)
    keys.add(`${idOf(entry?.pallet_id)}|${idOf(row.operator_id)}`)
    const arrival = findRow('rm_arrival', idOf(line?.arrival_id))
    if (arrival) arrivals.add(String(arrival.arrival_no))
  })
  return { packages: keys.size, arrivals: [...arrivals].sort() }
}

/** `GET /api/v1/wms/inbound-receiving/arrival-stage?ids=` — chip tahap per kedatangan untuk daftar (R-10). */
export function arrivalStages(ids: string[]) {
  return Object.fromEntries(ids.map(id => [id, arrivalStageSummary(id)]))
}
